import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateInfo, fetchDateInfo } from '../services/api';
import { parseLessonRoute, getSavedLanguage, lessonUrl, Language, lessonIdentityKey } from '../utils/lesson';

interface LessonContextType {
  language: Language;
  year: number;
  quarter: number;
  lessonNumber: number;
  isLoading: boolean;
  error: string | null;
  retry?: () => void;
  dateInfo?: DateInfo;
  rememberDateInfo: (data: DateInfo) => void;
}

const LessonContext = createContext<LessonContextType | undefined>(undefined);
export const useLessonContext = () => {
  const context = useContext(LessonContext);
  if (!context) throw new Error('useLessonContext must be used within a LessonProvider');
  return context;
};

export const LessonProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const route = parseLessonRoute(location.pathname);
  const [failure, setFailure] = useState<{ key: string; attempt: number; message: string } | null>(null);
  // Session metadata is shared across languages; current-entry routes still fetch today's date fresh.
  const [dates, setDates] = useState<Record<string, DateInfo>>({});
  const rememberDateInfo = useCallback((data: DateInfo) => {
    setDates(previous => ({ ...previous, [lessonIdentityKey(data)]: data }));
  }, []);
  const [attempt, setAttempt] = useState(0);
  const language = route.kind === 'invalid' ? 'en' : route.language ?? getSavedLanguage() ?? 'en';

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const current = parseLessonRoute(location.pathname);
    if (current.kind !== 'current') return;
    const now = new Date();
    fetchDateInfo(now.getFullYear(), now.getMonth() + 1, now.getDate(), controller.signal).then(data => {
      if (active) {
        rememberDateInfo(data);
        navigate(lessonUrl(language, data, window.location.hash), { replace: true });
      }
    }).catch(() => {
      if (active) setFailure({ key: location.key, attempt, message: 'Unable to find the current lesson. Please try again.' });
    });
    return () => { active = false; controller.abort(); };
  }, [location.pathname, location.key, language, attempt, navigate, rememberDateInfo]);

  const identity = route.kind === 'lesson' ? route.identity : null;
  const error = route.kind === 'current' && failure?.key === location.key && failure.attempt === attempt ? failure.message : null;
  return (
    <LessonContext.Provider value={{
      language,
      year: identity?.year ?? 0,
      quarter: identity?.quarter ?? 0,
      lessonNumber: identity?.lessonNum ?? 0,
      isLoading: route.kind === 'current' && !error,
      error: route.kind === 'invalid' ? 'Invalid lesson URL.' : error,
      retry: route.kind === 'current' ? () => setAttempt(value => value + 1) : undefined,
      dateInfo: identity ? dates[lessonIdentityKey(identity)] : undefined,
      rememberDateInfo
    }}>
      {children}
    </LessonContext.Provider>
  );
};
